import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import RawHTML from "../RawHTML";

// components
import EligibilityChecklist from "./EligibilityChecklist";
import FundReopenNotifications from "../fund-reopen-notifications/FundReopenNotifications";

/**
 * Convert an integer dollar amount to a USD currency including commas
 */
const numberToCurrency = (integer) => {
  return "$" + integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * The CareMetx API returns an integer for the FPL level. This must be converted to a percentage.
 */
const fpl = (integer) => {
  return `${integer.toString()}00%`;
};

/**
 * Format assistanceAmount string by wrapping non-dollar text in <small>
 */
const formatAssistanceAmount = (str) => {
  const parts = str.split(/(\$\d[\d,]*)/); // Split on dollar amounts

  return parts.map((part, index) => {
    if (/\$\d[\d,]*/.test(part)) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    } else if (part !== "") {
      const leadingSpace = part.startsWith(" ") ? " " : "";
      const trailingSpace = part.endsWith(" ") ? " " : "";
      const coreText = part.trim();
      return (
        <React.Fragment key={index}>
          {leadingSpace}
          <small>{coreText}</small>
          {trailingSpace}
        </React.Fragment>
      );
    } else {
      return null;
    }
  });
};

const DiseaseFundInfo = ({
  title,
  data,
  eligibilityCriteria,
  assistanceAmount,
  additionalAssistanceAmountInformation,
  jumpLinkAnchors,   // [{ label, id, href }]
  fundIntroTitle,
  fundIntro,
  eligibilityChecklistTitle,
  eligibilityItems
}) => {
  // Use anchors as the single source of truth
  const anchors = Array.isArray(jumpLinkAnchors) ? jumpLinkAnchors : [];

  // --- dispatch a custom event so the global SubNav script can (re)initialize ---
  useEffect(() => {
    if (typeof document !== "undefined") {
      setTimeout(function () {
        var evt;
        try {
          evt = new Event("jumpnav:mounted");
        } catch (e) {
          evt = document.createEvent("Event");
          evt.initEvent("jumpnav:mounted", true, true);
        }
        document.dispatchEvent(evt);
      }, 0);
    }
  }, [anchors && anchors.length]); // re-fire if anchors change

  // Index-based helpers (Bublé-safe)
  const jumpByIndex = (idx) => {
    if (typeof idx !== "number") return null;
    if (idx < 0 || idx >= anchors.length) return null;
    return anchors[idx];
  };
  const idForIndex = (idx) => {
    const j = jumpByIndex(idx);
    return j ? j.id : undefined; // use as element id (no '#')
  };

  const renderJumpLinks = () => {
    if (!anchors.length) return null;

    // Keeping your original markup
    return (
      <div className="JumpNav l-padding {{ has_post_thumbnail() ? 'has-image' : 'has-no-image'}}">
        <div className="l-wrap--wide">
          <div className="JumpNav-main">
            <button className="Subnav-about-toggle" type="button" data-expands="jumpnav" data-expands-height>
              <span className="Subnav-about-toggle-text">Jump to section</span>
            </button>

            <div className="JumpNav-menu Subnav-about-menu" id="jumpnav">
              <ul className="JumpNav-list Subnav-about-list">
                {anchors.map(function (j) {
                  return (
                    <li key={j.id} className="JumpNav-item page_item">
                      <a href={j.href}>{j.label}</a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Normalize fund status and derive "open" state
var rawStatus    = (data && data.Fund_Status) || "";
var statusStr    = String(rawStatus).toLowerCase().replace(/\u00a0/g, " ").trim();
var fundIsOpen   = statusStr === "open";
var fundIsClosed = statusStr === "closed, wait list available";

  // --- Wait list modal trigger without modifying FundReopenNotifications ---
  // We render the component (hidden) and click its internal button programmatically.
  const waitlistHostRef = useRef(null);
  const openWaitlist = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!waitlistHostRef.current) return;
    // Find the inner trigger button rendered by FundReopenNotifications and click it
    var btn = waitlistHostRef.current.querySelector("button.btn");
    if (btn && typeof btn.click === "function") {
      btn.click();
    }
  };

  const AssistanceAmount = () => {
    return (
      <div className="u-richtext f-scale-body">
        <h2>Assistance Amount</h2>
        {assistanceAmount && assistanceAmount.trim() !== "" ? (
          <p className="f-h3">{formatAssistanceAmount(assistanceAmount)}</p>
        ) : (
          <p className="f-h3">
            {numberToCurrency(parseInt(data.Assistance_Amount, 10))} <small>per year</small>
          </p>
        )}
        {additionalAssistanceAmountInformation && additionalAssistanceAmountInformation.trim() !== "" ? (
          <RawHTML html={additionalAssistanceAmountInformation} />
        ) : (
          <p>
            Patients may apply for additional assistance at the end of their
            eligibility period, subject to the availability of funding.
          </p>
        )}
      </div>
    );
  };

  return (
    <div>
      {renderJumpLinks()}
      <div className="l-padding">
        <div className="l-wrap">
          <div className="disease-additional-information">

            <div className="help-message">
              {fundIsOpen && (
                <a className="btn" target="_blank" rel="noopener" href="https://panapply.org/">Apply online</a>
              )}

              {fundIsClosed && (
                <>
                  {/* Visible link that opens the modal */}
                  <a className="btn" href="https://totalassist.org/notify" target="_blank" rel="noopener">Get notified when this fund opens</a>
                  {/* Hidden host for the modal trigger component */}
                  <span
                    ref={waitlistHostRef}
                    style={{ display: "none" }}
                    aria-hidden="true"
                  >
                    <FundReopenNotifications data={data} />
                  </span>
                </>
              )}
              <p>Need help by phone? Call 1-866-316-7263, Monday through Friday, 9 a.m. to 5:30 p.m. ET</p>
            </div>

            <h2>{fundIntroTitle}</h2>
            <div className="fund-intro-content u-richtext" dangerouslySetInnerHTML={{ __html: fundIntro }} />

            {/* Example: use the FIRST jump link's id on this section */}
            <div id={idForIndex(0)} className="eligibility-checker">
              <EligibilityChecklist
                fundTitle={title}
                checklistTitle={eligibilityChecklistTitle}
                items={eligibilityItems}
                fundStatus={statusStr}
                data={data}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DiseaseFundInfo.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  assistanceAmount: PropTypes.string,
  additionalAssistanceAmountInformation: PropTypes.string,
  jumpLinkAnchors: PropTypes.array, // [{label, id, href}] provided by parent
  fundIntroTitle: PropTypes.string,
  fundIntro: PropTypes.string,
  eligibilityChecklistTitle: PropTypes.string,
  eligibilityItems: PropTypes.array
};

DiseaseFundInfo.defaultProps = {
  jumpLinkAnchors: []
};

export default DiseaseFundInfo;
